import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { getAppMcpHandoff } from '../api';

type Props = {
  appId: string;
};

export default function McpHandoffCard({ appId }: Props) {
  const q = useQuery({
    queryKey: ['app-mcp-handoff', appId],
    queryFn: async () => getAppMcpHandoff(appId),
    enabled: Boolean(appId),
    staleTime: 5_000,
  });

  const markdown = String((q.data as any)?.markdown || '');
  const filename = String((q.data as any)?.filename || `${appId}-mcp-handoff.md`);

  const copyToClipboard = async () => {
    if (!markdown) return;
    await navigator.clipboard.writeText(markdown);
  };

  const downloadMarkdown = () => {
    if (!markdown) return;
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div>
          <div className="text-sm font-semibold">MCP Handoff Document</div>
          <div className="text-xs text-gray-600">Copy or download a complete third-party Cursor onboarding document.</div>
        </div>

        {q.isLoading && <div className="text-sm text-gray-600">Generating handoff document…</div>}
        {q.error && <div className="text-sm text-red-600">{(q.error as any)?.message || 'Failed to load handoff document'}</div>}

        {!q.isLoading && !q.error && (
          <>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={copyToClipboard} disabled={!markdown}>
                Copy to clipboard
              </Button>
              <Button variant="outline" onClick={downloadMarkdown} disabled={!markdown}>
                Download .md
              </Button>
              <Button variant="outline" onClick={() => q.refetch()} disabled={q.isFetching}>
                Refresh
              </Button>
            </div>
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Replace <span className="font-mono">YOUR_MCP_KEY_HERE</span> with the generated MCP key before sharing.
            </div>
            <pre className="text-[11px] whitespace-pre-wrap bg-gray-50 border rounded p-3 max-h-72 overflow-auto">{markdown}</pre>
          </>
        )}
      </CardContent>
    </Card>
  );
}
